class NotificationService {
  constructor() {
    this.settings = this.loadSettings();
    this.initializePushNotifications();
  }

  // Load notification settings from localStorage
  loadSettings() {
    const saved = localStorage.getItem('listWizNotificationSettings');
    return saved ? JSON.parse(saved) : {
      listShared: true,
      listUpdated: true,
      itemAdded: false,
      itemCompleted: false,
      pushNotifications: false,
      emailNotifications: true,
      inAppNotifications: true
    };
  }

  // Update settings
  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('listWizNotificationSettings', JSON.stringify(this.settings));
  }

  // Initialize push notification support
  async initializePushNotifications() {
    if ('Notification' in window && this.settings.pushNotifications) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        console.log('Notification permission:', permission);
      }
    }
  }

  // Send notification based on type and settings
  async sendNotification(type, data) {
    if (!this.settings[type]) {
      return; // Notification type is disabled
    }

    const message = this.formatMessage(type, data);
    
    // In-app notification
    if (this.settings.inAppNotifications) {
      this.showInAppNotification(message);
    }

    // Push notification
    if (this.settings.pushNotifications && 'Notification' in window && Notification.permission === 'granted') {
      this.showPushNotification(message);
    }

    // Email notification (would be handled by backend)
    if (this.settings.emailNotifications) {
      this.sendEmailNotification(type, data);
    }
  }

  // Format notification message based on type and data
  formatMessage(type, data) {
    const templates = {
      listShared: `${data.sharedBy} shared "${data.listName}" with you`,
      listUpdated: `${data.updatedBy} updated "${data.listName}"${data.changes ? ` - ${data.changes}` : ''}`,
      itemAdded: `New item "${data.itemName}" was added to "${data.listName}"${data.addedBy ? ` by ${data.addedBy}` : ''}`,
      itemCompleted: `${data.completedBy} completed "${data.itemName}" in "${data.listName}"`
    };

    return {
      title: 'ListWiz',
      body: templates[type] || 'You have a new notification',
      icon: '/favicon.ico',
      timestamp: new Date().toISOString(),
      data: data
    };
  }

  // Show in-app notification (modal or toast)
  showInAppNotification(message) {
    // This would integrate with your modal system
    console.log('In-app notification:', message);
    
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.className = 'notification-toast';
    toast.innerHTML = `
      <div class="notification-toast-content">
        <div class="notification-toast-icon">🔔</div>
        <div class="notification-toast-text">
          <div class="notification-toast-title">${message.title}</div>
          <div class="notification-toast-body">${message.body}</div>
        </div>
      </div>
    `;
    
    // Add styles
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      max-width: 300px;
      animation: slideInRight 0.3s ease;
    `;

    document.body.appendChild(toast);

    // Remove after 5 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 5000);

    // Add CSS animations if not already present
    if (!document.getElementById('notification-animations')) {
      const style = document.createElement('style');
      style.id = 'notification-animations';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        .notification-toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .notification-toast-icon {
          font-size: 20px;
        }
        .notification-toast-title {
          font-weight: 600;
          font-size: 14px;
          color: #1a202c;
        }
        .notification-toast-body {
          font-size: 12px;
          color: #4a5568;
          line-height: 1.4;
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Show browser push notification
  showPushNotification(message) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(message.title, {
        body: message.body,
        icon: message.icon,
        badge: message.icon,
        data: message.data,
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        // Could navigate to specific list or item
      };

      // Auto-close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  }

  // Send email notification (backend integration)
  async sendEmailNotification(type, data) {
    try {
      // This would be an API call to your backend
      await fetch('/api/notifications/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          type,
          data,
          settings: this.settings
        })
      });
    } catch (error) {
      console.error('Failed to send email notification:', error);
    }
  }

  // Helper methods for common notification scenarios
  notifyListShared(listName, sharedByUser) {
    this.sendNotification('listShared', {
      listName,
      sharedBy: sharedByUser.name || sharedByUser.email
    });
  }

  notifyListUpdated(listName, updatedByUser, changes) {
    this.sendNotification('listUpdated', {
      listName,
      updatedBy: updatedByUser.name || updatedByUser.email,
      changes
    });
  }

  notifyItemAdded(itemName, listName, addedByUser) {
    this.sendNotification('itemAdded', {
      itemName,
      listName,
      addedBy: addedByUser?.name || addedByUser?.email
    });
  }

  notifyItemCompleted(itemName, listName, completedByUser) {
    this.sendNotification('itemCompleted', {
      itemName,
      listName,
      completedBy: completedByUser.name || completedByUser.email
    });
  }

  // Test notification
  testNotification(type, testMessage) {
    if (!this.settings.inAppNotifications && !this.settings.pushNotifications) {
      throw new Error('Please enable at least one notification method to test');
    }

    const testData = {
      listShared: { listName: 'Grocery List', sharedBy: 'John' },
      listUpdated: { listName: 'Weekend Plans', updatedBy: 'Mary', changes: '2 new items added' },
      itemAdded: { itemName: 'Milk', listName: 'Grocery List', addedBy: 'John' },
      itemCompleted: { itemName: 'Buy groceries', listName: 'Weekend Plans', completedBy: 'Sarah' }
    };

    // Override message if provided
    if (testMessage) {
      const message = {
        title: 'ListWiz',
        body: testMessage,
        icon: '/favicon.ico',
        timestamp: new Date().toISOString(),
        data: testData[type]
      };

      if (this.settings.inAppNotifications) {
        this.showInAppNotification(message);
      }

      if (this.settings.pushNotifications && 'Notification' in window && Notification.permission === 'granted') {
        this.showPushNotification(message);
      }
    } else {
      this.sendNotification(type, testData[type]);
    }
  }

  // Request notification permissions
  async requestPermissions() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.updateSettings({ pushNotifications: true });
      }
      return permission;
    }
    return 'not-supported';
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window;
  }

  // Get current permission status
  getPermissionStatus() {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'not-supported';
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
